/* Build in libraries */
import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';

/* Third party libraries */
import { XIcon } from '@heroicons/react/outline';
import { LocationMarkerIcon } from '@heroicons/react/solid';

/* Components */
import Card from 'components/UI/Card';
import { CRApprovedDashboard } from './CRApprovedDashboard';
import { PhoneNumberFormatDisplay } from 'components/UI/NumberFormatDisplay/PhoneNumberFormatDisplay';
import LocationMarker from 'components/UI/GoogleMap/LocationMarker';
import GoogleMap from 'components/UI/GoogleMap';

/* Services */
import { CRAllApplications } from 'features/ApplicationForm/FormConfig/CR/CRDashboard/CRAllApplications';
import { useApplicationsGeoLocation } from './useApplicatiosGeoLocation';

/* Assets */

interface IApplicationDashboardComponentProps {
  data: any;
  defaultView?: 'ALL' | 'APPROVED';
  hideSwitch?: boolean;
}

export const CRDashboard = ({
  data,
  defaultView = 'ALL',
  hideSwitch,
}: IApplicationDashboardComponentProps) => {
  const location = useLocation();
  const [selectedProject, setSelectedProject] = useState<any>();
  const [viewStatus, setViewStatus] = useState<string>(defaultView);

  const { applicationsGeoLocated, widgetsData } =
    useApplicationsGeoLocation(data);

  return (
    <>
      <div className='max-w-screen-3xl mx-auto'>
        {!hideSwitch && (
          <div className='font-semibold text-bold py-4 rounded-md space-x-2'>
            <span
              className={`${
                viewStatus === 'ALL'
                  ? 'bg-primaryColor-normal'
                  : 'bg-neutralColor-light hover:bg-primaryColor-normal'
              } text-white bg-primaryColor-normal px-4 py-2 cursor-pointer`}
              onClick={() => setViewStatus('ALL')}
            >
              All Applications
            </span>
            <span
              className={`${
                viewStatus === 'APPROVED'
                  ? 'bg-primaryColor-normal'
                  : 'bg-neutralColor-light hover:bg-primaryColor-normal'
              } text-white bg-primaryColor-normal px-4 py-2 cursor-pointer`}
              onClick={() => setViewStatus('APPROVED')}
            >
              Approved Applications
            </span>
          </div>
        )}

        {viewStatus === 'ALL' && (
          <CRAllApplications
            data={widgetsData}
            applicationsGeoLocated={applicationsGeoLocated}
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
          />
        )}
        {viewStatus === 'APPROVED' && (
          <CRApprovedDashboard data={data}>
            <Card title='Project Location'>
              {applicationsGeoLocated.length === 0 && (
                <div className='w-full flex justify-center items-center h-screen/2'>
                  <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                    <LocationMarkerIcon className='h-8 w-8' />
                    <h1>No Projects</h1>
                  </div>
                  <p>There are no assigned projects to display.</p>
                </div>
              )}
              {applicationsGeoLocated.length > 0 && (
                <div className='w-full grid grid-cols-6 h-screen/2'>
                  <div className='col-span-6 w-full border-l p-5'>
                    {applicationsGeoLocated[0] && (
                      <GoogleMap
                        defaultCenter={{
                          lat: applicationsGeoLocated[0].coordinates.lat,
                          lng: applicationsGeoLocated[0].coordinates.lng,
                        }}
                      >
                        {applicationsGeoLocated?.map((res: any) => {
                          return (
                            <LocationMarker
                              key={res?.id}
                              lat={res?.coordinates.lat}
                              lng={res?.coordinates.lng}
                            >
                              <div className='relative'>
                                {selectedProject &&
                                  selectedProject?.id === res?.id && (
                                    <div
                                      onClick={() =>
                                        setSelectedProject(undefined)
                                      }
                                      className='bg-white border rounded absolute p-3 bottom-4 left-5 w-56'
                                    >
                                      <XIcon className='h-4 w-4 absolute right-1 top-1 text-neutralColor-normal hover:text-red-500' />
                                      <Link
                                        to={`${location.pathname.replace(
                                          'dashboard',
                                          'applications'
                                        )}/${
                                          selectedProject?.id
                                        }/application_details`}
                                      >
                                        <p className='font-semibold text-primaryColor-normal underline'>
                                          {selectedProject?.name}
                                        </p>
                                      </Link>
                                      <p className='text-xs'>
                                        {selectedProject?.fullAddress}
                                      </p>
                                      <div className='mt-2 text-sm flex justify-between gap-2'>
                                        <span>Email</span>
                                        <span className='font-semibold'>
                                          {selectedProject?.email}
                                        </span>
                                      </div>
                                      <div className='mt-2 text-sm flex justify-between gap-2'>
                                        <span>Phone</span>
                                        <span className='font-semibold'>
                                          <PhoneNumberFormatDisplay
                                            value={
                                              selectedProject?.phone || 'none'
                                            }
                                          />
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                {res && (
                                  <LocationMarkerIcon
                                    onClick={() => setSelectedProject(res)}
                                    className={
                                      selectedProject?.id === res?.id
                                        ? 'h-8 w-8 text-black'
                                        : 'h-8 w-8 text-red-500 '
                                    }
                                  />
                                )}
                              </div>
                            </LocationMarker>
                          );
                        })}
                      </GoogleMap>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </CRApprovedDashboard>
        )}
      </div>
    </>
  );
};
